/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

'use client';

import { memo, useCallback, useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useDebounce } from '@uidotdev/usehooks';

import { Skeleton } from '@marketplace/gather-ui/components';

import useSearchAutoComplete from '@/rest/hooks/use-search-auto-complete';
import { useSearchDropdown } from '../search-dropdown.provider';
import { type UpdateSearchValueParams } from '../../search.component';
import { Suggestion } from './suggestion.component';

const styleList = { listStyle: 'none' };

type SuggestionsProps = {
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
  value: string;
};

export const Suggestions = memo(function Suggestions({
  updateSearchValue,
  value,
}: SuggestionsProps): ReactNode {
  const { closeDropdown } = useSearchDropdown();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const debouncedValue = useDebounce(value, 500);
  const autoCompleteState = useSearchAutoComplete(debouncedValue);

  // dedupe suggestions so unique makes are returned
  const suggestions = useMemo(() => {
    const data = autoCompleteState.data ?? [];

    return [...new Set(data)];
  }, [autoCompleteState.data]);

  const isLoadingSuggestions = autoCompleteState.isFetching || autoCompleteState.isLoading;

  // highlight matching text in suggestions
  const highlightMatch = useCallback(
    (suggestion: string): ReactNode => {
      if (!value) {
        return suggestion;
      }
      const lowerSuggestion = suggestion.toLowerCase();
      const lowerQuery = value.toLowerCase();
      const matchIndex = lowerSuggestion.indexOf(lowerQuery);

      if (matchIndex === -1) {
        return suggestion;
      }

      const before = suggestion.slice(0, matchIndex);
      const match = suggestion.slice(matchIndex, matchIndex + value.length);
      const after = suggestion.slice(matchIndex + value.length);

      return (
        <>
          {before}
          <strong>{match}</strong>
          {after}
        </>
      );
    },
    [value]
  );

  const selectSuggestion = useCallback(
    async (suggestion: string): Promise<void> => {
      await updateSearchValue({
        searchValue: suggestion,
        searchType: 'PREDICTIVE_SEARCH',
        selectedFilter: null,
        autoSubmit: true,
      });
      closeDropdown();
    },
    [closeDropdown, updateSearchValue]
  );

  if (isLoadingSuggestions) {
    return (
      <Stack spacing={4}>
        <Skeleton preventDelay height={24} variant='rounded' width='65%' />
        <Skeleton preventDelay height={24} variant='rounded' width='35%' />
        <Skeleton preventDelay height={24} variant='rounded' width='65%' />
        <Skeleton preventDelay height={24} variant='rounded' width='35%' />
        <Skeleton preventDelay height={24} variant='rounded' width='65%' />
      </Stack>
    );
  }

  return (
    <Box aria-label='Search suggestions' component='ul' m={0} p={0} role='listbox' sx={styleList}>
      {suggestions.map((suggestion) => (
        <Suggestion
          highlightMatch={highlightMatch}
          isMobile={isMobile}
          key={suggestion}
          selectSuggestion={selectSuggestion}
          suggestion={suggestion}
        />
      ))}
    </Box>
  );
});
